import React from 'react';

import { DEFAULT_CURRENCY } from '../constants';
import { cellKey, fmtDate, bulkCellValue } from '../utils';
import {
  Grid,
  Cell,
  FeeValue,
  FeeInput,
  Reference,
  Validity,
  WindowBadge,
  Audit,
  FieldError,
  Empty,
} from '../styled-comps';

// Read-only cell: the representative window's fee, validity and (if present) audit line.
const ViewCell = ({ entry }) => {
  if (!entry) return <Empty>—</Empty>;
  const { row, count } = entry;
  const { last_changed_by: changedBy, last_changed_on: changedOn } = row;
  return (
    <Cell>
      <FeeValue>
        {row.currency || DEFAULT_CURRENCY} {row.fee_per_class}
      </FeeValue>
      <Validity>
        {fmtDate(row.validity_start)} → {fmtDate(row.validity_end) || 'open'}
      </Validity>
      {count > 1 && <WindowBadge>{count} windows</WindowBadge>}
      {(changedBy || changedOn) && (
        <Audit>
          {changedBy ? `by ${changedBy}` : ''}
          {changedBy && changedOn ? ' · ' : ''}
          {changedOn ? fmtDate(changedOn) : ''}
        </Audit>
      )}
    </Cell>
  );
};

// Editable cell: current rate as a reference, plus the fee input and any collision error.
const BulkCell = ({ entry, cellErr, value, disabled, onChange }) => {
  const current = entry ? entry.row.fee_per_class : null;
  const currency = entry ? entry.row.currency || DEFAULT_CURRENCY : DEFAULT_CURRENCY;
  return (
    <Cell>
      {current != null && (
        <Reference>
          now: {currency} {current}
        </Reference>
      )}
      <FeeInput
        type="number"
        min="1"
        step="1"
        value={value}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
      />
      {cellErr && <FieldError>{cellErr}</FieldError>}
    </Cell>
  );
};

// The rates grid: grade ranges as rows, tiers as columns. View mode renders each cell's
// representative window; bulk mode renders an editable fee input per cell.
const PayoutGrid = ({
  isBulk,
  activeRegion,
  visibleGradeRanges,
  visibleTiers,
  cellData,
  bulkValues,
  bulkErrors,
  bulkSubmitting,
  onBulkChange,
}) => (
  <Grid>
    <thead>
      <tr>
        <th>Grade</th>
        {visibleTiers.map(tier => (
          <th key={tier}>{tier}</th>
        ))}
      </tr>
    </thead>
    <tbody>
      {visibleGradeRanges.map(gradeRange => (
        <tr key={gradeRange}>
          <td className="row-head">{gradeRange}</td>
          {visibleTiers.map(tier => {
            const key = cellKey(activeRegion, gradeRange, tier);
            const entry = cellData[key];
            return (
              <td key={tier}>
                {isBulk ? (
                  <BulkCell
                    entry={entry}
                    cellErr={bulkErrors[key]}
                    value={bulkCellValue(
                      bulkValues,
                      key,
                      entry ? entry.row.fee_per_class : null,
                    )}
                    disabled={bulkSubmitting}
                    onChange={value => onBulkChange(key, value)}
                  />
                ) : (
                  <ViewCell entry={entry} />
                )}
              </td>
            );
          })}
        </tr>
      ))}
    </tbody>
  </Grid>
);

export default PayoutGrid;
